// Bootstrap entry for the Vite SPA.
//
// **What this file does:**
//   1. Build the QueryClient + tRPC client pair via `createTrpcReactClient`.
//   2. Mount the React tree wrapped in:
//        <trpc.Provider client={...} queryClient={...}>
//          <QueryClientProvider client={queryClient}>
//            <App />
//          </QueryClientProvider>
//        </trpc.Provider>
//   3. That's it. No service worker. No PWA. No tracking script.
//
// **Why we wrap with `<StrictMode>`:** React 18's strict mode helps
// surface effect leaks during development by double-invoking effects.
// It's a dev-only optimization (no production impact).
//
// **Cookie boot timing:** the `AuthGate` fires `whoAmI` immediately.
// On 401 → redirect to `/login`. The boot path doesn't probe the
// network for anything else first — keeping cold-start fast.

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { QueryClientProvider } from '@tanstack/react-query';

import { App } from './app.js';
import { bffEndpoints } from './auth/bff-origin.js';
import { PreviewFeaturesProvider } from './prefs/index.js';
import { createTrpcReactClient, trpc } from './trpc-client.js';

import './styles.css';
// Smokeball design-system layer — imported AFTER styles.css so it wins the
// transitional class collisions (e.g. `.page`) while old pages migrate.
import './design/design-system.css';
// fn-47.5 — the ui-react package styles (design tokens + `sbui-*`
// component classes, scoped under `.sbui-root`). Imported ONCE at app
// entry; the inbox pages supply the `.sbui-root` wrapper.
import '@sb-ai-platform/ui-react/styles.css';

const root = document.getElementById('root');
if (root === null) {
  throw new Error('main.tsx: #root not found in index.html');
}

// fn-45.4 — in deployed (cross-origin) mode the BFF is on a separate origin
// from the SPA, so the tRPC base must be the ABSOLUTE `${VITE_BFF_URL}/trpc`
// or the browser would POST to the SPA origin (no BFF, no `__Host-` cookies).
// In dev, `VITE_BFF_URL` is unset → relative `/trpc` via the Vite proxy.
const endpoints = bffEndpoints();
const { queryClient, trpcClient } = createTrpcReactClient({ trpcUrl: endpoints.trpcUrl });

createRoot(root).render(
  <StrictMode>
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        {/* fn-72.1 — app-wide client preferences (preview-features flag +
            agent favorites, localStorage-backed). No auth dependency, so it
            wraps the whole tree; the hooks throw if a consumer calls them
            outside this provider. */}
        <PreviewFeaturesProvider>
          <App />
        </PreviewFeaturesProvider>
      </QueryClientProvider>
    </trpc.Provider>
  </StrictMode>,
);
